import React, { useEffect, useMemo, useState } from 'react';
import DefaultMediaFallback from '../../components/common/DefaultMediaFallback';
import { moduleCommonStrings, tvShowSearchStrings } from '../../content/hobbyModules';
import { getPublicErrorMessage } from '../../utils/errors';

/**
 * Performs the show episode preview operation.
 *
 * @param {*} show - show value.
 * @returns {*} The derived operation result.
 */
function showEpisodePreview(show) {
  const seasons = show.seasons || [];
  const episodes = seasons.flatMap((season) => season.episodes || []);
  const episodeCount = seasons.reduce(
    (total, season) => total + Number(season.episodeCount || season.episodes?.length || 0),
    0
  );
  const firstEpisode = episodes[0];

  return {
    episodeCount,
    firstEpisode: firstEpisode
      ? `S${firstEpisode.seasonNumber} E${firstEpisode.episodeNumber}${firstEpisode.title ? ` - ${firstEpisode.title}` : ''}`
      : '',
  };
}

/**
 * Renders the tvshow add search experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.results - results value.
 * @param {*} props.onCancel - Callback invoked when cancel occurs.
 * @param {*} props.onManualEntry - Callback invoked when manual entry occurs.
 * @param {*} props.onSearch - Callback invoked when search occurs.
 * @param {*} props.onUseShow - Callback invoked when use show occurs.
 * @param {*} props.isSearching - Whether is searching is enabled.
 * @param {*} props.error - Failure being normalized or reported.
 * @returns {JSX.Element} The rendered interface.
 */
export default function TVShowAddSearch({ results, onCancel, onManualEntry, onSearch, onUseShow, isSearching, error }) {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= 2;

  useEffect(() => {
    if (!canSearch) return undefined;
    const timer = window.setTimeout(() => onSearch(trimmedQuery), 250);
    return () => window.clearTimeout(timer);
  }, [canSearch, onSearch, trimmedQuery]);

  const visibleResults = useMemo(() => (canSearch && !isSearching ? results : []), [canSearch, isSearching, results]);

  return (
    <div className="movie-add-search">
      <button type="button" className="recipe-back-link" onClick={onCancel}>← Back</button>

      <section className="recipe-form-section priority-section">
        <div className="form-group">
          <label>{tvShowSearchStrings.label}</label>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={tvShowSearchStrings.placeholder}
            autoFocus
          />
        </div>

        {isSearching && <p className="muted-copy movie-search-status">{moduleCommonStrings.actions.searching}</p>}
        {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

        <div className="movie-import-results movie-add-results">
          {visibleResults.map((show) => {
            const episodePreview = showEpisodePreview(show);
            return (
              <button type="button" className="movie-import-result" key={show.tvdbId} onClick={() => onUseShow(show)}>
                {show.posterUrl ? (
                  <img src={show.posterUrl} alt="" />
                ) : (
                  <DefaultMediaFallback className="movie-poster-placeholder" fallbackType="tvshows" />
                )}
                <div>
                  <h3>{show.title}</h3>
                  <p>{[show.firstAirYear, show.network, show.creator && `Created by ${show.creator}`].filter(Boolean).join(' - ')}</p>
                  <p>{show.overview}</p>
                  {episodePreview.episodeCount > 0 && (
                    <p className="movie-card-detail-line">
                      {episodePreview.episodeCount} episodes{episodePreview.firstEpisode ? ` • Starts with ${episodePreview.firstEpisode}` : ''}
                    </p>
                  )}
                  <div className="recipe-tags">
                    {(show.genres || []).map((genre) => <span key={genre}>{genre}</span>)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {canSearch && !isSearching && visibleResults.length === 0 && (
          <div className="empty-state movie-import-empty">
            <p>{tvShowSearchStrings.empty}</p>
            <button type="button" className="btn-secondary" onClick={onManualEntry}>{moduleCommonStrings.actions.addManually}</button>
          </div>
        )}

        <p className="muted-copy movie-search-status">{tvShowSearchStrings.attribution}</p>
      </section>
    </div>
  );
}
